import React, { useState } from 'react';
import { AlertCircle, AlertTriangle, Info, Clock, CheckCircle, User, RefreshCw, AlertOctagon } from 'lucide-react';
import { Alert } from '@/types';

interface AlertsTableProps {
  alerts: Alert[];
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
}

const AlertsTable: React.FC<AlertsTableProps> = ({ alerts, loading = false, error = null, onRefresh }) => {
  const [selectedAlert, setSelectedAlert] = useState<string | null>(null);

  const getAlertIcon = (type: Alert['type']) => {
    switch (type) {
      case 'Critical':
        return <AlertCircle size={16} className="text-red-500" />;
      case 'Warning':
        return <AlertTriangle size={16} className="text-yellow-500" />;
      case 'Info':
        return <Info size={16} className="text-blue-500" />;
      default:
        return <Info size={16} className="text-blue-500" />;
    }
  };

  const getSeverityColor = (severity: Alert['severity']) => {
    switch (severity) {
      case 'Critical':
        return 'text-red-500 bg-red-500/10';
      case 'High':
        return 'text-orange-500 bg-orange-500/10';
      case 'Medium':
        return 'text-yellow-500 bg-yellow-500/10';
      case 'Low':
        return 'text-blue-500 bg-blue-500/10';
      default:
        return 'text-text-muted bg-navy-border/10';
    }
  };

  const getStatusIcon = (status: Alert['status']) => {
    switch (status) {
      case 'Open':
        return <AlertCircle size={14} className="text-red-500" />;
      case 'Acknowledged':
        return <Clock size={14} className="text-yellow-500" />;
      case 'Resolved':
        return <CheckCircle size={14} className="text-green-500" />;
      default:
        return <AlertCircle size={14} className="text-text-muted" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 24 * 60) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInMinutes / (24 * 60));
      return `${days}d ago`;
    }
  };

  const handleRowClick = (alertId: string) => {
    setSelectedAlert(selectedAlert === alertId ? null : alertId);
  };

  // Loading State
  if (loading) {
    return (
      <div className="bg-navy-card border border-navy-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-heading">Recent Alerts</h3>
          <RefreshCw size={16} className="text-text-muted animate-spin" />
        </div>
        
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="flex items-center space-x-4">
                <div className="h-4 w-16 bg-navy-border rounded"></div>
                <div className="h-4 w-20 bg-navy-border rounded"></div>
                <div className="flex-1 h-4 bg-navy-border rounded"></div>
                <div className="h-4 w-24 bg-navy-border rounded"></div>
                <div className="h-4 w-16 bg-navy-border rounded"></div>
                <div className="h-4 w-20 bg-navy-border rounded"></div>
                <div className="h-4 w-20 bg-navy-border rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="bg-navy-card border border-navy-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-heading">Recent Alerts</h3>
          {onRefresh && (
            <button 
              onClick={onRefresh}
              className="p-2 text-text-muted hover:text-text-heading transition-colors"
              aria-label="Refresh alerts"
            >
              <RefreshCw size={16} />
            </button>
          )}
        </div>
        
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertOctagon size={48} className="text-red-500 mb-4" />
          <h4 className="text-lg font-medium text-text-heading mb-2">Failed to Load Alerts</h4>
          <p className="text-text-muted mb-4 max-w-md">{error}</p>
          {onRefresh && (
            <button 
              onClick={onRefresh}
              className="px-4 py-2 bg-gold-primary text-navy-primary rounded-md font-medium hover:bg-gold-hover transition-colors"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  // Empty State
  if (!alerts || alerts.length === 0) {
    return (
      <div className="bg-navy-card border border-navy-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-heading">Recent Alerts</h3>
          {onRefresh && (
            <button 
              onClick={onRefresh}
              className="p-2 text-text-muted hover:text-text-heading transition-colors"
              aria-label="Refresh alerts"
            >
              <RefreshCw size={16} />
            </button>
          )}
        </div>
        
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <CheckCircle size={48} className="text-green-500 mb-4" />
          <h4 className="text-lg font-medium text-text-heading mb-2">No Recent Alerts</h4>
          <p className="text-text-muted">All systems are operating normally.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-navy-card border border-navy-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-text-heading">Recent Alerts</h3>
        {onRefresh && (
          <button 
            onClick={onRefresh}
            className="p-2 text-text-muted hover:text-text-heading transition-colors"
            aria-label="Refresh alerts"
          >
            <RefreshCw size={16} />
          </button>
        )}
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-navy-border">
              <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Time</th>
              <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Alert</th>
              <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Description</th>
              <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">System</th>
              <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Severity</th>
              <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Status</th>
              <th className="text-left py-3 px-2 text-sm font-medium text-text-muted">Assignee</th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((alert) => (
              <React.Fragment key={alert.id}>
                <tr 
                  className={`
                    border-b border-navy-border/50 cursor-pointer transition-colors
                    hover:bg-navy-border/20 
                    ${selectedAlert === alert.id ? 'bg-navy-border/30' : ''}
                  `}
                  onClick={() => handleRowClick(alert.id)}
                >
                  <td className="py-3 px-2">
                    <span className="text-sm text-text-muted">
                      {formatTimestamp(alert.timestamp)}
                    </span>
                  </td>
                  
                  <td className="py-3 px-2">
                    <div className="flex items-center space-x-2">
                      {getAlertIcon(alert.type)}
                      <span className="text-sm font-medium text-text-primary">
                        {alert.type}
                      </span>
                    </div>
                  </td>
                  
                  <td className="py-3 px-2">
                    <span className="text-sm text-text-primary">
                      {alert.description.length > 60 
                        ? `${alert.description.substring(0, 60)}...` 
                        : alert.description
                      }
                    </span>
                  </td>
                  
                  <td className="py-3 px-2">
                    <span className="text-sm text-text-primary font-medium">
                      {alert.affectedSystem}
                    </span>
                  </td>
                  
                  <td className="py-3 px-2">
                    <span className={`
                      text-xs px-2 py-1 rounded-full font-medium
                      ${getSeverityColor(alert.severity)}
                    `}>
                      {alert.severity}
                    </span>
                  </td>
                  
                  <td className="py-3 px-2">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(alert.status)}
                      <span className="text-sm text-text-primary">
                        {alert.status}
                      </span>
                    </div>
                  </td>
                  
                  <td className="py-3 px-2">
                    {alert.assignee ? (
                      <div className="flex items-center space-x-2">
                        <User size={14} className="text-text-muted" />
                        <span className="text-sm text-text-primary">
                          {alert.assignee}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-text-muted">Unassigned</span>
                    )}
                  </td>
                </tr>
                
                {/* Expanded row for selected alert */}
                {selectedAlert === alert.id && (
                  <tr className="bg-navy-border/10">
                    <td colSpan={7} className="py-4 px-2">
                      <div className="pl-6 space-y-3">
                        <div>
                          <h4 className="text-sm font-medium text-text-heading mb-2">Full Description</h4>
                          <p className="text-sm text-text-primary">{alert.description}</p>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-text-muted">Created:</span>
                            <span className="text-text-primary ml-2">
                              {new Date(alert.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <div>
                            <span className="text-text-muted">Alert ID:</span>
                            <span className="text-text-primary ml-2 font-mono">{alert.id}</span>
                          </div>
                          <div>
                            <span className="text-text-muted">System:</span>
                            <span className="text-text-primary ml-2">{alert.affectedSystem}</span>
                          </div>
                        </div>
                        
                        <div className="flex space-x-2">
                          <button className="px-3 py-1 bg-gold-primary text-navy-primary text-sm rounded button">
                            Acknowledge
                          </button>
                          <button className="px-3 py-1 bg-navy-border text-text-primary text-sm rounded button">
                            Assign
                          </button>
                          <button className="px-3 py-1 bg-green-600 text-white text-sm rounded button">
                            Resolve
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Summary Stats */}
      <div className="mt-4 pt-4 border-t border-navy-border">
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-lg font-bold text-red-500">
              {alerts.filter(a => a.status === 'Open').length}
            </p>
            <p className="text-xs text-text-muted">Open</p>
          </div>
          <div>
            <p className="text-lg font-bold text-yellow-500">
              {alerts.filter(a => a.status === 'Acknowledged').length}
            </p>
            <p className="text-xs text-text-muted">Acknowledged</p>
          </div>
          <div>
            <p className="text-lg font-bold text-green-500">
              {alerts.filter(a => a.status === 'Resolved').length}
            </p>
            <p className="text-xs text-text-muted">Resolved</p>
          </div>
          <div>
            <p className="text-lg font-bold text-gold-primary">
              {alerts.filter(a => a.severity === 'Critical').length}
            </p>
            <p className="text-xs text-text-muted">Critical</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertsTable;